import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Heart, History, User, FileText, Crown, Settings, Filter, Bookmark, Folder, BookmarkPlus } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PromptGrid from "@/components/prompts/PromptGrid";
import LoadingState from "@/components/explore/LoadingState";
import SubscriptionStatus from "@/components/dashboard/SubscriptionStatus";
import { useAuth } from "@/context/AuthContext";
import { getUserPrompts, getLikedPrompts, getRecentlyUsedPrompts } from "@/services/promptService";
import { getUserSubscription, getUserUsage } from "@/services/subscriptionService";
import { 
  getSavedPrompts, 
  getPromptCollections, 
  createCollection,
  updateCollection,
  deleteCollection,
  getCollectionPrompts,
  SavedPrompt,
  PromptCollection 
} from "@/services/savedPromptService";
import { supabase } from "@/integrations/supabase/client";
import type { Prompt } from "@/services/promptService";
import type { UserSubscription, UserUsage } from "@/components/admin/types";
import PromptUpgradeDialog from "@/components/prompts/PromptUpgradeDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const EmptyState = ({ message, action }: { message: string; action?: React.ReactNode }) => (
  <div className="flex flex-col items-center justify-center py-20">
    <Bookmark className="h-16 w-16 text-muted-foreground/30 mb-4" />
    <p className="text-muted-foreground text-lg mb-4">{message}</p>
    {action}
  </div>
);

const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || "saved";
  
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [userPrompts, setUserPrompts] = useState<Prompt[]>([]);
  const [likedPrompts, setLikedPrompts] = useState<Prompt[]>([]);
  const [recentPrompts, setRecentPrompts] = useState<Prompt[]>([]);
  const [savedPrompts, setSavedPrompts] = useState<SavedPrompt[]>([]);
  const [collections, setCollections] = useState<PromptCollection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<PromptCollection | null>(null);
  const [collectionPrompts, setCollectionPrompts] = useState<SavedPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hideRejected, setHideRejected] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [usage, setUsage] = useState<UserUsage | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewCollectionDialog, setShowNewCollectionDialog] = useState(false);
  const [showEditCollectionDialog, setShowEditCollectionDialog] = useState(false);
  const [collectionName, setCollectionName] = useState("");
  const [collectionDescription, setCollectionDescription] = useState("");
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (activeTab !== defaultTab) {
      searchParams.set('tab', activeTab);
      setSearchParams(searchParams);
    }
  }, [activeTab, defaultTab, searchParams, setSearchParams]);
  
  useEffect(() => {
    if (!user) {
      console.log("No user logged in, redirecting to sign-in");
      navigate('/sign-in');
      return;
    }
    
    console.log(`Dashboard: Loading data for user ${user.id}, active tab: ${activeTab}`);
    
    const loadUserData = async () => {
      setIsLoading(true);
      setLoadError(null);
      
      try {
        const userSubscription = await getUserSubscription(user.id);
        setSubscription(userSubscription);
        
        const userUsage = await getUserUsage(user.id);
        setUsage(userUsage);
        
        if (activeTab === "myPrompts") {
          const prompts = await getUserPrompts(user.id);
          setUserPrompts(prompts);
        } 
        else if (activeTab === "liked") {
          const liked = await getLikedPrompts(user.id);
          setLikedPrompts(liked);
        } 
        else if (activeTab === "history") {
          const recent = await getRecentlyUsedPrompts(user.id);
          setRecentPrompts(recent);
        }
        else if (activeTab === "saved") {
          console.log(`Dashboard: Fetching saved prompts for user ${user.id}`);
          const saved = await getSavedPrompts(false);
          console.log(`Dashboard: Received ${saved.length} saved prompts`);
          setSavedPrompts(saved);
          
          if (saved.length === 0) {
            console.log("No saved prompts found, checking if user has any saved prompts in database");
            
            try {
              const { count, error } = await supabase
                .from("user_saved_prompts")
                .select("id", { count: "exact", head: true })
                .eq("user_id", user.id);
              
              if (error) {
                console.error("Error checking saved prompts count:", error);
              } else {
                console.log(`Database check: User has ${count} saved prompts`);
              }
            } catch (err) {
              console.error("Error during additional saved prompts check:", err);
            }
          }
        }
        else if (activeTab === "collections") {
          const collectionsData = await getPromptCollections();
          setCollections(collectionsData);
          
          if (selectedCollection) {
            const stillExists = collectionsData.find(c => c.id === selectedCollection.id);
            if (stillExists) {
              setSelectedCollection(stillExists);
              const collectionPrompts = await getCollectionPrompts(selectedCollection.id);
              setCollectionPrompts(collectionPrompts);
            } else {
              setSelectedCollection(null);
              setCollectionPrompts([]);
            }
          }
        }
      } catch (error: any) {
        console.error("Error loading dashboard data:", error);
        setLoadError(error.message || "Failed to load your dashboard data");
        toast.error("There was a problem loading your dashboard data");
      } finally {
        setIsLoading(false);
      }
    };
    
    loadUserData();
  }, [user, navigate, activeTab, selectedCollection?.id]);

  const usagePercentage = usage?.prompt_copies_limit 
    ? Math.min(100, (usage.prompt_copies_used / usage.prompt_copies_limit) * 100)
    : 0;
  
  const isOverLimit = usage?.prompt_copies_used > (usage?.prompt_copies_limit || 0);

  const filteredUserPrompts = hideRejected 
    ? userPrompts.filter(prompt => prompt.status !== 'rejected') 
    : userPrompts;
    
  const formattedUsage = usage ? {
    canCopy: !isOverLimit,
    usedCount: usage.prompt_copies_used,
    limit: usage.prompt_copies_limit,
    percentUsed: usagePercentage
  } : null;
  
  const filterPrompts = (prompts: SavedPrompt[]) => {
    if (!searchTerm.trim()) return prompts;
    
    const term = searchTerm.toLowerCase();
    return prompts.filter(prompt => 
      (prompt.custom_title?.toLowerCase() || prompt.prompt.title.toLowerCase()).includes(term) ||
      prompt.prompt.description.toLowerCase().includes(term) ||
      prompt.prompt.content.toLowerCase().includes(term)
    );
  };
  
  const handleCreateCollection = async () => {
    if (!collectionName.trim()) return;
    
    const newCollection = await createCollection(collectionName, collectionDescription);
    if (newCollection) {
      setCollections(prev => [...prev, newCollection]);
      setShowNewCollectionDialog(false);
      setCollectionName("");
      setCollectionDescription("");
    }
  };

  const handleEditCollection = async () => {
    if (!selectedCollection || !collectionName.trim()) return;
    
    const updated = await updateCollection(selectedCollection.id, {
      name: collectionName,
      description: collectionDescription
    });
    
    if (updated) {
      const updatedCollections = collections.map(c => 
        c.id === selectedCollection.id ? { ...c, name: collectionName, description: collectionDescription } : c
      );
      setCollections(updatedCollections);
      setSelectedCollection({ ...selectedCollection, name: collectionName, description: collectionDescription });
      setShowEditCollectionDialog(false);
    }
  };

  const handleDeleteCollection = async () => {
    if (!selectedCollection) return;
    
    const success = await deleteCollection(selectedCollection.id);
    if (success) {
      const remainingCollections = collections.filter(c => c.id !== selectedCollection.id);
      setCollections(remainingCollections);
      setSelectedCollection(null);
      setCollectionPrompts([]);
    }
  };

  const openEditCollectionDialog = () => {
    if (!selectedCollection) return;
    
    setCollectionName(selectedCollection.name);
    setCollectionDescription(selectedCollection.description || "");
    setShowEditCollectionDialog(true);
  };

  const handleSelectCollection = async (collection: PromptCollection) => {
    setSelectedCollection(collection);
    setIsLoading(true);
    
    try {
      const prompts = await getCollectionPrompts(collection.id);
      setCollectionPrompts(prompts);
    } catch (error) {
      console.error("Error loading collection prompts:", error);
      toast.error("Could not load prompts in this collection");
    } finally {
      setIsLoading(false);
    }
  };

  const backToCollections = () => {
    setSelectedCollection(null);
    setCollectionPrompts([]);
  };

  // Prepare saved prompts for displaying in PromptGrid
  const savedPromptsForGrid = savedPrompts.map(savedPrompt => ({
    id: savedPrompt.prompt.id,
    title: savedPrompt.custom_title || savedPrompt.prompt.title,
    description: savedPrompt.prompt.description,
    content: savedPrompt.prompt.content,
    category: savedPrompt.prompt.category.name,
    createdAt: savedPrompt.saved_at,
    author: { 
      name: savedPrompt.prompt.author_name || "Unknown", 
      id: savedPrompt.prompt.user_id || "" 
    },
    likes: 0,
    reviews: 0,
    copies: 0
  }));

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <div className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <header className="mb-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold">Dashboard</h1>
                <p className="mt-2 text-muted-foreground">
                  Manage your prompts, collections, and account
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-4 sm:mt-0">
                <Button asChild size="sm" variant="outline">
                  <Link to={`/user/${user?.id}`} className="flex items-center gap-1">
                    <Settings className="h-4 w-4" />
                    Edit Profile
                  </Link>
                </Button>
                <Button asChild className="mt-2 sm:mt-0" size="sm">
                  <Link to="/create" className="flex items-center gap-1">
                    <Plus className="h-4 w-4" />
                    Submit Prompt
                  </Link>
                </Button>
              </div>
            </div>
          </header>
          
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-border/40 overflow-hidden animate-fade-in">
            <div className="p-6 border-b border-border/40">
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 rounded-full p-5">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">{user?.user_metadata?.first_name || user?.email}</h2>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString() : ""}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-b border-border/40">
              <SubscriptionStatus />
            </div>
            
            <div className="px-6 pt-6 pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search your prompts and collections..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <Tabs 
              defaultValue={activeTab} 
              value={activeTab} 
              onValueChange={setActiveTab}
              className="w-full"
            >
              <div className="px-6 border-b border-border/40">
                <TabsList className="justify-start bg-transparent h-14 p-0 gap-8">
                  <TabsTrigger 
                    value="saved"
                    className="flex items-center gap-2 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-0"
                  >
                    <Bookmark className="h-4 w-4" />
                    <span>Saved</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="collections"
                    className="flex items-center gap-2 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-0"
                  >
                    <Folder className="h-4 w-4" />
                    <span>Collections</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="history"
                    className="flex items-center gap-2 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-0"
                  >
                    <History className="h-4 w-4" />
                    <span>History</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="myPrompts"
                    className="flex items-center gap-2 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-0"
                  >
                    <FileText className="h-4 w-4" />
                    <span>My Prompts</span>
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <div className="p-6">
                <TabsContent value="saved" className="mt-0">
                  {isLoading ? (
                    <LoadingState />
                  ) : loadError ? (
                    <div className="text-center py-12 border border-dashed border-red-300 rounded-lg">
                      <p className="text-red-500 mb-4">{loadError}</p>
                      <Button onClick={() => window.location.reload()}>Retry</Button>
                    </div>
                  ) : savedPromptsForGrid.length > 0 ? (
                    <div>
                      <p className="text-muted-foreground mb-6">
                        Showing {savedPromptsForGrid.length} saved prompts
                      </p>
                      <PromptGrid 
                        prompts={filterPrompts(savedPrompts).map(savedPrompt => ({
                          id: savedPrompt.prompt.id,
                          title: savedPrompt.custom_title || savedPrompt.prompt.title,
                          description: savedPrompt.prompt.description,
                          content: savedPrompt.prompt.content,
                          category: savedPrompt.prompt.category.name,
                          createdAt: savedPrompt.saved_at,
                          author: { 
                            name: savedPrompt.prompt.author_name || "Unknown", 
                            id: savedPrompt.prompt.user_id || "" 
                          },
                          likes: 0,
                          reviews: 0,
                          copies: 0
                        }))}
                        emptyMessage="No saved prompts match your search."
                      />
                    </div>
                  ) : (
                    <EmptyState 
                      message="You haven't saved any prompts yet." 
                      action={
                        <Button asChild>
                          <Link to="/explore" className="flex items-center gap-2">
                            <BookmarkPlus className="h-4 w-4" />
                            Browse Prompts to Save
                          </Link>
                        </Button>
                      }
                    />
                  )}
                </TabsContent>
                
                <TabsContent value="collections" className="mt-0">
                  {isLoading ? (
                    <LoadingState />
                  ) : selectedCollection ? (
                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={backToCollections}
                            className="mb-2"
                          >
                            ← Back to Collections
                          </Button>
                          <h2 className="text-2xl font-bold">{selectedCollection.name}</h2>
                          {selectedCollection.description && (
                            <p className="mt-1 text-muted-foreground">{selectedCollection.description}</p>
                          )}
                        </div>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="icon">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={openEditCollectionDialog}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                              Edit Collection
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={handleDeleteCollection}
                              className="text-destructive"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                              Delete Collection
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      <Separator className="mb-6" />
                      
                      {collectionPrompts.length > 0 ? (
                        <PromptGrid 
                          prompts={filterPrompts(collectionPrompts).map(savedPrompt => ({
                            id: savedPrompt.prompt.id,
                            title: savedPrompt.custom_title || savedPrompt.prompt.title,
                            description: savedPrompt.prompt.description,
                            content: savedPrompt.prompt.content,
                            category: savedPrompt.prompt.category.name,
                            createdAt: savedPrompt.saved_at,
                            author: { name: "Unknown", id: "" },
                            likes: 0,
                            reviews: 0,
                            copies: 0
                          }))}
                        />
                      ) : (
                        <EmptyState 
                          message="This collection is empty." 
                          action={
                            <Button asChild>
                              <Link to="/explore" className="flex items-center gap-2">
                                <Plus className="h-4 w-4" />
                                Add Prompts to Collection
                              </Link>
                            </Button>
                          }
                        />
                      )}
                    </div>
                  ) : collections.length > 0 ? (
                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold">Your Collections</h2>
                        <Button onClick={() => setShowNewCollectionDialog(true)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/><path d="M12 10v6"/><path d="M9 13h6"/></svg>
                          New Collection
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {collections.map((collection) => (
                          <div 
                            key={collection.id}
                            className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-border/40 p-6 hover:shadow-md transition-all duration-300 cursor-pointer"
                            onClick={() => handleSelectCollection(collection)}
                          >
                            <div className="flex justify-between items-start mb-4">
                              <div className="bg-primary/10 rounded-full p-3">
                                <Folder className="h-6 w-6 text-primary" />
                              </div>
                            </div>
                            
                            <h3 className="text-lg font-semibold mb-2">{collection.name}</h3>
                            
                            {collection.description && (
                              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                                {collection.description}
                              </p>
                            )}
                            
                            <div className="text-sm text-muted-foreground">
                              {collection.prompts_count} {collection.prompts_count === 1 ? 'prompt' : 'prompts'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <EmptyState 
                      message="You don't have any collections yet." 
                      action={
                        <Button onClick={() => setShowNewCollectionDialog(true)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/><path d="M12 10v6"/><path d="M9 13h6"/></svg>
                          Create Your First Collection
                        </Button>
                      }
                    />
                  )}
                </TabsContent>
                
                <TabsContent value="history" className="mt-0">
                  {isLoading ? (
                    <LoadingState />
                  ) : recentPrompts.length > 0 ? (
                    <PromptGrid prompts={recentPrompts} />
                  ) : (
                    <EmptyState 
                      message="You haven't used any prompts yet." 
                      action={
                        <Button asChild>
                          <Link to="/explore" className="flex items-center gap-2">
                            <Search className="h-4 w-4 mr-1" />
                            Explore Prompts
                          </Link>
                        </Button>
                      }
                    />
                  )}
                </TabsContent>
                
                <TabsContent value="myPrompts" className="mt-0">
                  <div className="flex justify-end mb-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setHideRejected(!hideRejected)}
                      className="flex items-center gap-2"
                    >
                      <Filter className="h-4 w-4" />
                      {hideRejected ? "Show Rejected" : "Hide Rejected"}
                    </Button>
                  </div>
                  
                  {isLoading ? (
                    <LoadingState />
                  ) : filteredUserPrompts.length > 0 ? (
                    <div>
                      <PromptGrid 
                        prompts={filteredUserPrompts.map(prompt => ({
                          ...prompt,
                          title: prompt.status !== 'approved' ? 
                            `${prompt.title} ${prompt.status === 'pending' ? '(Pending)' : 
                             prompt.status === 'rejected' ? '(Rejected)' : ''}` : 
                            prompt.title
                        }))} 
                      />
                    </div>
                  ) : userPrompts.length > 0 && filteredUserPrompts.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground mb-4">
                        All your prompts are currently hidden by filters.
                      </p>
                      <Button onClick={() => setHideRejected(false)}>
                        Show All Prompts
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground mb-4">You haven't created any prompts yet.</p>
                      <Button asChild>
                        <Link to="/create-prompt">Submit Your First Prompt</Link>
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </div>
      
      <Footer />
      
      <PromptUpgradeDialog 
        open={showUpgradeDialog} 
        onOpenChange={setShowUpgradeDialog}
        usageInfo={formattedUsage}
      />
      
      <Dialog open={showNewCollectionDialog} onOpenChange={setShowNewCollectionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Collection</DialogTitle>
            <DialogDescription>
              Create a new collection to organize your saved prompts.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="collection-name">Collection Name</Label>
              <Input
                id="collection-name"
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
                placeholder="My Awesome Collection"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="collection-description">Description (Optional)</Label>
              <Textarea
                id="collection-description"
                value={collectionDescription}
                onChange={(e) => setCollectionDescription(e.target.value)}
                placeholder="What kind of prompts will you store here?"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCollectionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCollection} 
              disabled={!collectionName.trim()}
            >
              Create Collection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showEditCollectionDialog} onOpenChange={setShowEditCollectionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Collection</DialogTitle>
            <DialogDescription>
              Update your collection details.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-collection-name">Collection Name</Label>
              <Input
                id="edit-collection-name"
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit-collection-description">Description (Optional)</Label>
              <Textarea
                id="edit-collection-description"
                value={collectionDescription}
                onChange={(e) => setCollectionDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCollectionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditCollection} 
              disabled={!collectionName.trim()}
            >
              Update Collection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
